import { atom } from 'jotai';
import type { User } from 'firebase/auth';
import type { Job, Finding, ModuleRun, Report, Severity } from '@/types/forensics';

// Auth state
export const currentUserAtom = atom<User | null>(null);
export const authLoadingAtom = atom<boolean>(true);

// Upload state
export const selectedFileAtom = atom<File | null>(null);
export const analysisModeAtom = atom<'standard' | 'deep'>('standard');
export const autoDeleteAtom = atom<boolean>(false);

// Current job state
export const currentJobAtom = atom<Job | null>(null);
export const moduleRunsAtom = atom<ModuleRun[]>([]);
export const findingsAtom = atom<Finding[]>([]);
export const reportSummaryAtom = atom<Report['summary'] | null>(null);
export const metadataSummaryAtom = atom<Record<string, unknown>>({});

// Preview URL and media type (persists across navigation)
export const previewUrlAtom = atom<string | null>(null);
export const previewMediaTypeAtom = atom<'image' | 'video'>('image');

// Viewer state
export const selectedFindingIdAtom = atom<string | null>(null);
export const visibleLayersAtom = atom<Set<string>>(new Set(['bounding_boxes']));
export const viewerZoomAtom = atom<number>(1);

// Findings filter state
export const severityFilterAtom = atom<Severity | null>(null);
export const minConfidenceFilterAtom = atom<number>(0);

// Derived atoms
export const selectedFindingAtom = atom((get) => {
  const id = get(selectedFindingIdAtom);
  const findings = get(findingsAtom);
  return findings.find((f) => f.id === id) ?? null;
});

export const filteredFindingsAtom = atom((get) => {
  const findings = get(findingsAtom);
  const severity = get(severityFilterAtom);
  const minConf = get(minConfidenceFilterAtom);

  return findings.filter((f) => {
    if (severity && f.severity !== severity) return false;
    if (f.confidence < minConf) return false;
    return true;
  });
});

export const findingsGroupedBySeverityAtom = atom((get) => {
  const findings = get(filteredFindingsAtom);
  const groups: Record<string, Finding[]> = {
    high: [],
    medium: [],
    low: [],
    info: [],
  };
  for (const f of findings) {
    groups[f.severity].push(f);
  }
  return groups;
});
